import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { useFinanceAuth } from "@/context/FinanceAuthContext";
import { COSTOFISSO } from "@/constants/testIds";
import { toast } from "sonner";
import { Plus, X, Trash2, PieChart as PieIcon, History, Copy, LayoutTemplate } from "lucide-react";
import { codiceCostoFisso } from "@/lib/codici";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import RipartizioneMensile from "@/pages/costi/RipartizioneMensile";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const CATEGORIE = [
  ["stipendi_amministrativi","Stipendi amministrativi"],
  ["affitti","Affitti"],
  ["utenze","Utenze"],
  ["assicurazioni","Assicurazioni"],
  ["consulenze","Consulenze"],
  ["commercialista","Commercialista"],
  ["software","Software"],
  ["licenze","Licenze"],
  ["telefonia","Telefonia"],
  ["mezzi_aziendali","Mezzi aziendali"],
  ["leasing","Leasing"],
  ["noleggi","Noleggi"],
  ["finanziamenti","Finanziamenti"],
  ["manutenzioni","Manutenzioni"],
  ["pubblicita","Pubblicità"],
  ["sicurezza","Sicurezza"],
  ["formazione","Formazione"],
  ["imposte","Imposte"],
  ["altri","Altri costi fissi"],
];
const PER = [["mensile","Mensile"],["bimestrale","Bimestrale"],["trimestrale","Trimestrale"],["semestrale","Semestrale"],["annuale","Annuale"],["una_tantum","Una tantum"]];
const COLORS = ["#ea580c","#0f172a","#2563eb","#16a34a","#eab308","#7c3aed","#dc2626","#0891b2","#db2777","#64748b"];

// ------- TEMPLATE VOCI RICORRENTI ETHOS -------
// Ogni voce = campo pre-compilato; l'utente può poi modificare importo + salvare.
const TEMPLATES_ETHOS = [
  // Stipendi
  { descrizione: "Stipendio Manuel", categoria: "stipendi_amministrativi", periodicita: "mensile", scadenza_giorno: 27, importo_imponibile: 0, aliquota_iva: 0 },
  { descrizione: "Stipendio Giusy", categoria: "stipendi_amministrativi", periodicita: "mensile", scadenza_giorno: 27, importo_imponibile: 0, aliquota_iva: 0 },
  { descrizione: "Stipendio Alessia", categoria: "stipendi_amministrativi", periodicita: "mensile", scadenza_giorno: 27, importo_imponibile: 0, aliquota_iva: 0 },
  { descrizione: "Stipendio Mattia", categoria: "stipendi_amministrativi", periodicita: "mensile", scadenza_giorno: 27, importo_imponibile: 0, aliquota_iva: 0 },
  { descrizione: "Stipendio Ilaria", categoria: "stipendi_amministrativi", periodicita: "mensile", scadenza_giorno: 27, importo_imponibile: 0, aliquota_iva: 0 },
  { descrizione: "Stipendio Alberto", categoria: "stipendi_amministrativi", periodicita: "mensile", scadenza_giorno: 27, importo_imponibile: 0, aliquota_iva: 0 },
  { descrizione: "Buste paga (operai)", categoria: "stipendi_amministrativi", periodicita: "mensile", scadenza_giorno: 27, importo_imponibile: 0, aliquota_iva: 0 },
  // Professionali
  { descrizione: "Commercialista", categoria: "commercialista", periodicita: "trimestrale", scadenza_giorno: 20, importo_imponibile: 0, aliquota_iva: 22 },
  // Immobili
  { descrizione: "Affitto", categoria: "affitti", periodicita: "mensile", scadenza_giorno: 5, importo_imponibile: 0, aliquota_iva: 0 },
  { descrizione: "Capannone", categoria: "affitti", periodicita: "mensile", scadenza_giorno: 5, importo_imponibile: 0, aliquota_iva: 0 },
  // Utenze e ufficio
  { descrizione: "Bollette / Utenze", categoria: "utenze", periodicita: "bimestrale", scadenza_giorno: 15, importo_imponibile: 0, aliquota_iva: 22 },
  { descrizione: "Materiale ufficio", categoria: "altri", periodicita: "mensile", scadenza_giorno: 15, importo_imponibile: 0, aliquota_iva: 22 },
  // Mezzi
  { descrizione: "Auto Manuel", categoria: "mezzi_aziendali", periodicita: "mensile", scadenza_giorno: 10, importo_imponibile: 0, aliquota_iva: 22 },
  { descrizione: "Rate furgoni", categoria: "leasing", periodicita: "mensile", scadenza_giorno: 10, importo_imponibile: 0, aliquota_iva: 22 },
  { descrizione: "Telepass", categoria: "mezzi_aziendali", periodicita: "mensile", scadenza_giorno: 20, importo_imponibile: 0, aliquota_iva: 22 },
  { descrizione: "Multe", categoria: "mezzi_aziendali", periodicita: "una_tantum", scadenza_giorno: 15, importo_imponibile: 0, aliquota_iva: 0 },
  // Assicurazioni
  { descrizione: "Polizza RC", categoria: "assicurazioni", periodicita: "annuale", scadenza_giorno: 1, importo_imponibile: 0, aliquota_iva: 0 },
  { descrizione: "Polizza mezzi", categoria: "assicurazioni", periodicita: "annuale", scadenza_giorno: 1, importo_imponibile: 0, aliquota_iva: 0 },
];

export default function CostiFissi() {
  const { user } = useAuth();
  const { pw, unlocked, verifyAndSet } = useFinanceAuth();
  const [fatturatoInput, setFatturatoInput] = useState("");
  const [passwordFinanziaria, setPasswordFinanziaria] = useState("");
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tipoNuovoCosto, setTipoNuovoCosto] = useState("fisso");
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState(new Set());
  const [historyFor, setHistoryFor] = useState(null); // costo aperto nella modale storico
  const [form, setForm] = useState({
    descrizione: "",
    categoria: "affitti",
    importo_imponibile: 0,
    aliquota_iva: 22,
    periodicita: "mensile",
    data_inizio: "",
    data_fine: "",
    scadenza_giorno: 1,
    societa: "",
    metodo_pagamento: "",
    note: "",
    active: true,
  });

  const [showCessati, setShowCessati] = React.useState(false);
  const { data: items = [] } = useQuery({
    queryKey: ["costi-fissi", showCessati],
    queryFn: () => api.get("/costi-fissi", { params: { include_cessati: showCessati } }).then((r) => r.data),
    enabled: isAdmin(user),
  });
  const { data: summary } = useQuery({
    queryKey: ["costi-fissi-summary"],
    queryFn: () => api.get("/costi-fissi/summary").then((r) => r.data),
    enabled: isAdmin(user),
  });

  const salvaFatturato = useMutation({
    mutationFn: (importo) =>
      api.put(
        "/admin/situazione-economica/fatturato-previsto",
        { importo },
        { headers: { "X-Finance-Password": pw } }
      ),
    onSuccess: () => {
      toast.success("Fatturato annuo previsto aggiornato");
      setFatturatoInput("");
      qc.invalidateQueries({ queryKey: ["costi-fissi-summary"] });
      qc.invalidateQueries({ queryKey: ["fatturato-annuale-previsto"] });
      qc.invalidateQueries({ queryKey: ["situazione-completa"] });
      qc.invalidateQueries({ queryKey: ["cantieri"] });
      qc.invalidateQueries({ queryKey: ["cantiere"] });
    },
    onError: (e) =>
      toast.error(e.response?.data?.detail || "Errore nel salvataggio del fatturato"),
  });

  const create = useMutation({
    mutationFn: (payload) => api.post("/costi-fissi", payload).then((r) => r.data),
    onSuccess: () => {
      toast.success("Costo aggiunto correttamente");
      qc.invalidateQueries({ queryKey: ["costi-fissi"] });
      qc.invalidateQueries({ queryKey: ["costi-fissi-summary"] });
      setOpen(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const createBulk = useMutation({
    mutationFn: async (templates) => {
      const results = [];
      for (const t of templates) {
        try {
          const r = await api.post("/costi-fissi", {
            ...t,
            data_inizio: new Date().toISOString().slice(0, 10),
            societa: "",
            metodo_pagamento: "",
            note: "Creato da template",
            active: true,
          });
          results.push({ ok: true, data: r.data });
        } catch (e) {
          results.push({ ok: false, error: e.response?.data?.detail || "errore", descrizione: t.descrizione });
        }
      }
      return results;
    },
    onSuccess: (results) => {
      const ok = results.filter((r) => r.ok).length;
      const ko = results.length - ok;
      if (ok > 0) toast.success(`Aggiunte ${ok} voci dal template${ko > 0 ? ` (${ko} errori)` : ""}`);
      if (ko > 0) toast.error(`${ko} voci non create — controlla se esistono già`);
      qc.invalidateQueries({ queryKey: ["costi-fissi"] });
      qc.invalidateQueries({ queryKey: ["costi-fissi-summary"] });
      setShowTemplates(false);
      setSelectedTemplates(new Set());
    },
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/costi-fissi/${id}`),
    onSuccess: (res) => {
      const mode = res?.data?.mode;
      if (mode === "soft") {
        toast.success(`Costo cessato al ${res.data.data_fine}. I mesi precedenti restano nella ripartizione storica.`);
      } else {
        toast.success("Costo eliminato definitivamente");
      }
      qc.invalidateQueries({ queryKey: ["costi-fissi"] });
      qc.invalidateQueries({ queryKey: ["costi-fissi-summary"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const restore = useMutation({
    mutationFn: (id) => api.post(`/costi-fissi/${id}/ripristina`),
    onSuccess: () => {
      toast.success("Costo ripristinato");
      qc.invalidateQueries({ queryKey: ["costi-fissi"] });
      qc.invalidateQueries({ queryKey: ["costi-fissi-summary"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const classificaGestione = useMutation({
    mutationFn: ({ costo_id, in_costi_gestione }) =>
      api.patch(`/costi-fissi/${costo_id}/classificazione-gestione`, {
        in_costi_gestione,
      }),
    onSuccess: () => {
      toast.success("Classificazione dei costi di gestione aggiornata");
      qc.invalidateQueries({ queryKey: ["costi-fissi"] });
      qc.invalidateQueries({ queryKey: ["costi-fissi-summary"] });
      qc.invalidateQueries({ queryKey: ["cantieri"] });
      qc.invalidateQueries({ queryKey: ["cantiere"] });
    },
    onError: (e) =>
      toast.error(e.response?.data?.detail || "Errore nella classificazione"),
  });

  const pieData = summary ? Object.entries(summary.per_categoria).map(([k, v]) => ({ name: catLabel(k), value: v })) : [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Amministrazione</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black">Costi di gestione</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 select-none cursor-pointer px-3 py-2 border border-slate-300 rounded-sm hover:bg-slate-50" data-testid="toggle-mostra-cessati">
            <input type="checkbox" checked={showCessati} onChange={(e) => setShowCessati(e.target.checked)} className="accent-orange-600" />
            Mostra cessati
          </label>
          <button
            onClick={() => setShowTemplates(true)}
            data-testid="btn-templates-costi-fissi"
            className="flex items-center gap-2 px-4 py-2 border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold rounded-sm"
          >
            <LayoutTemplate className="w-4 h-4" /> Aggiungi voci da template
          </button>
          <button
            onClick={() => { setTipoNuovoCosto("fisso"); setOpen(true); }}
            data-testid={COSTOFISSO.newButton}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-sm shadow-brutal"
          >
            <Plus className="w-4 h-4" /> Nuovo costo fisso
          </button>
          <button
            onClick={() => { setTipoNuovoCosto("gestione"); setOpen(true); }}
            data-testid="btn-nuovo-costo-gestione"
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-700 text-white font-semibold rounded-sm"
          >
            <Plus className="w-4 h-4" /> Nuovo costo di gestione
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <StatCard label="Costi fissi mensili senza IVA · BEP" value={eur(summary.mensile)} highlight />
          <StatCard label="Costi fissi annuali senza IVA · BEP" value={eur(summary.annuale)} />
          <StatCard label="Costi di gestione mensili senza IVA" value={eur(summary.mensile_gestione)} />
          <StatCard label="Costi di gestione annuali senza IVA" value={eur(summary.annuale_gestione)} />
          <StatCard label="Fatturato annuo previsto senza IVA" value={eur(summary.fatturato_previsto)} />
          <StatCard label="Incidenza costi di gestione" value={summary.incidenza_pct == null ? "—" : `${summary.incidenza_pct.toFixed(2)}%`} />
        </div>
      )}

      {summary && (
        <div className="bg-white border border-slate-200 p-5 space-y-4">
          <div>
            <div className="overline">Previsione economica annuale</div>
            <h2 className="text-lg font-bold text-slate-900">Fatturato previsto senza IVA</h2>
            <p className="text-sm text-slate-500 mt-1">
              Questo valore determina l'incidenza dei costi di gestione nei calcoli del guadagno di cantieri e manutenzioni.
            </p>
          </div>
          {!unlocked ? (
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await verifyAndSet(passwordFinanziaria);
                  setPasswordFinanziaria("");
                  toast.success("Area finanziaria sbloccata");
                } catch {
                  toast.error("Password finanziaria non valida");
                }
              }}
            >
              <label className="flex-1 min-w-[220px] text-sm font-semibold text-slate-700">
                Password finanziaria
                <input
                  type="password"
                  value={passwordFinanziaria}
                  onChange={(e) => setPasswordFinanziaria(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2"
                  autoComplete="current-password"
                  required
                />
              </label>
              <button type="submit" className="px-4 py-2 bg-slate-900 text-white font-semibold rounded-sm">
                Sblocca modifica
              </button>
            </form>
          ) : (
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                const importo = Number(fatturatoInput);
                if (!Number.isFinite(importo) || importo <= 0) {
                  toast.error("Inserisci un fatturato maggiore di zero");
                  return;
                }
                salvaFatturato.mutate(importo);
              }}
            >
              <label className="flex-1 min-w-[220px] text-sm font-semibold text-slate-700">
                Fatturato annuo previsto (€ senza IVA)
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={fatturatoInput}
                  onChange={(e) => setFatturatoInput(e.target.value)}
                  placeholder={String(summary.fatturato_previsto ?? "")}
                  className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2"
                  required
                />
              </label>
              <button
                type="submit"
                disabled={salvaFatturato.isPending}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-semibold rounded-sm"
              >
                {salvaFatturato.isPending ? "Salvataggio..." : "Salva fatturato"}
              </button>
            </form>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200">
          <div className="p-4 border-b border-slate-200"><div className="overline">Elenco costi fissi</div></div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-3">Descrizione</th>
                <th className="text-left p-3">Categoria</th>
                <th className="text-left p-3">Codice</th>
                <th className="text-left p-3">Periodicità</th>
                <th className="text-right p-3">Importo</th>
                <th className="text-right p-3">Con IVA</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.filter((c) => (c.tipo_costo || "fisso") === "fisso").map((c) => {
                const isCessato = !!c.cessato_at;
                return (
                <tr key={c.costo_id} className={`border-t border-slate-100 ${isCessato ? "bg-slate-50 text-slate-500" : ""}`}>
                  <td className="p-3 font-medium">
                    {c.descrizione}
                    {isCessato && (
                      <span className="ml-2 inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700 rounded-sm" data-testid={`badge-cessato-${c.costo_id}`}>
                        Cessato fino {c.data_fine}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-xs">{catLabel(c.categoria)}</td>
                  <td className="p-3 font-mono font-bold text-orange-700 text-xs">{codiceCostoFisso(c.categoria)}</td>
                  <td className="p-3 text-xs">{PER.find(p=>p[0]===c.periodicita)?.[1]}</td>
                  <td className="p-3 text-right font-mono">{eur(c.importo_imponibile)}</td>
                  <td className="p-3 text-right font-mono font-bold">
                    {eur(c.importo_imponibile * (1 + c.aliquota_iva/100))}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {c.periodicita !== "una_tantum" && (
                      <button
                        onClick={() => setHistoryFor(c)}
                        data-testid={`btn-storico-${c.costo_id}`}
                        title="Modifica importi mese per mese (storico protetto)"
                        className="inline-flex items-center gap-1 mr-3 px-2 py-1 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-sm"
                      >
                        <History className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Modifica mensile</span>
                      </button>
                    )}
                    {isCessato ? (
                      <button
                        onClick={() => restore.mutate(c.costo_id)}
                        data-testid={`btn-ripristina-${c.costo_id}`}
                        title="Ripristina costo"
                        className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold"
                      >
                        Ripristina
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (window.confirm("Cessare questo costo fisso?\n\nIl costo continuerà a comparire nelle ripartizioni dei mesi passati e del mese corrente, ma non verrà più conteggiato dal mese successivo.")) {
                            del.mutate(c.costo_id);
                          }
                        }}
                        data-testid={`btn-elimina-${c.costo_id}`}
                        title="Cessa costo (soft-delete)"
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
              {items.filter((c) => (c.tipo_costo || "fisso") === "fisso").length === 0 && <tr><td colSpan="7" className="p-8 text-center text-slate-400">Nessun costo fisso</td></tr>}
            </tbody>
          </table>
        </div>

        {historyFor && (
          <ImportiStoricoModal
            costo={historyFor}
            onClose={() => setHistoryFor(null)}
          />
        )}

        <div className="bg-white border border-slate-200 p-5">
          <div className="overline mb-3 flex items-center gap-2"><PieIcon className="w-4 h-4" /> Ripartizione categorie</div>
          <div className="w-full h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {pieData.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => eur(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
          <div className="p-4 border-b border-slate-200"><div className="overline">Elenco costi di gestione</div></div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-3">Descrizione</th>
                <th className="text-left p-3">Categoria</th>
                <th className="text-left p-3">Codice</th>
                <th className="text-left p-3">Periodicità</th>
                <th className="text-right p-3">Importo</th>
                <th className="text-right p-3">Con IVA</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.filter((c) => c.tipo_costo === "gestione").map((c) => {
                const isCessato = !!c.cessato_at;
                return (
                <tr key={c.costo_id} className={`border-t border-slate-100 ${isCessato ? "bg-slate-50 text-slate-500" : ""}`}>
                  <td className="p-3 font-medium">
                    {c.descrizione}
                    {isCessato && (
                      <span className="ml-2 inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700 rounded-sm" data-testid={`badge-cessato-gestione-${c.costo_id}`}>
                        Cessato fino {c.data_fine}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-xs">{catLabel(c.categoria)}</td>
                  <td className="p-3 font-mono font-bold text-orange-700 text-xs">{codiceCostoFisso(c.categoria)}</td>
                  <td className="p-3 text-xs">{PER.find(p=>p[0]===c.periodicita)?.[1]}</td>
                  <td className="p-3 text-right font-mono">{eur(c.importo_imponibile)}</td>
                  <td className="p-3 text-right font-mono font-bold">
                    {eur(c.importo_imponibile * (1 + c.aliquota_iva/100))}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {c.periodicita !== "una_tantum" && (
                      <button
                        onClick={() => setHistoryFor(c)}
                        data-testid={`btn-storico-gestione-${c.costo_id}`}
                        title="Modifica importi mese per mese (storico protetto)"
                        className="inline-flex items-center gap-1 mr-3 px-2 py-1 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-sm"
                      >
                        <History className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Modifica mensile</span>
                      </button>
                    )}
                    {isCessato ? (
                      <button
                        onClick={() => restore.mutate(c.costo_id)}
                        data-testid={`btn-ripristina-gestione-${c.costo_id}`}
                        title="Ripristina costo"
                        className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold"
                      >
                        Ripristina
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (window.confirm("Cessare questo costo fisso?\n\nIl costo continuerà a comparire nelle ripartizioni dei mesi passati e del mese corrente, ma non verrà più conteggiato dal mese successivo.")) {
                            del.mutate(c.costo_id);
                          }
                        }}
                        data-testid={`btn-elimina-gestione-${c.costo_id}`}
                        title="Cessa costo (soft-delete)"
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
              {items.filter((c) => c.tipo_costo === "gestione").length === 0 && (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-slate-400">
                    Nessun costo di gestione presente. Usa il pulsante Nuovo costo di gestione.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
      </div>

      {/* Ripartizione mensile con export PDF */}
      <RipartizioneMensile variante="fissi" />

      {/* Modale Template — aggiunta veloce voci ricorrenti Ethos */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowTemplates(false)}>
          <div className="bg-white w-full max-w-3xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="modal-templates-costi-fissi">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="font-display font-bold text-xl flex items-center gap-2">
                  <LayoutTemplate className="w-5 h-5 text-slate-500" /> Aggiungi voci da template
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Seleziona le voci ricorrenti che vuoi aggiungere. Verranno create con importo <b>0</b> —
                  potrai poi aggiornarle una per una con l'importo reale.
                </p>
              </div>
              <button onClick={() => setShowTemplates(false)}><X className="w-5 h-5 text-slate-500" /></button>
            </div>

            <div className="p-3 border-b border-slate-200 flex items-center gap-2 text-xs">
              <button onClick={() => setSelectedTemplates(new Set(TEMPLATES_ETHOS.map((_, i) => i)))}
                className="px-3 py-1.5 border border-slate-300 hover:bg-slate-50 rounded-sm font-semibold">
                Seleziona tutte
              </button>
              <button onClick={() => setSelectedTemplates(new Set())}
                className="px-3 py-1.5 border border-slate-300 hover:bg-slate-50 rounded-sm font-semibold">
                Deseleziona tutte
              </button>
              <span className="ml-auto font-mono text-slate-600">
                {selectedTemplates.size} / {TEMPLATES_ETHOS.length} selezionate
              </span>
            </div>

            <div className="p-3 max-h-[50vh] overflow-y-auto">
              <table className="w-full text-xs" data-testid="table-templates">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="w-8"></th>
                    <th className="text-left p-2">Descrizione</th>
                    <th className="text-left p-2">Categoria</th>
                    <th className="text-left p-2">Periodicità</th>
                    <th className="text-center p-2">Giorno</th>
                  </tr>
                </thead>
                <tbody>
                  {TEMPLATES_ETHOS.map((t, idx) => {
                    const already = items.some((i) => i.descrizione.toLowerCase() === t.descrizione.toLowerCase() && i.active);
                    const selected = selectedTemplates.has(idx);
                    return (
                      <tr key={idx}
                        className={`border-t border-slate-100 ${selected ? "bg-orange-50" : ""} ${already ? "opacity-50" : "hover:bg-slate-50 cursor-pointer"}`}
                        onClick={() => {
                          if (already) return;
                          const s = new Set(selectedTemplates);
                          if (s.has(idx)) s.delete(idx); else s.add(idx);
                          setSelectedTemplates(s);
                        }}
                        data-testid={`template-row-${idx}`}
                      >
                        <td className="p-2 text-center">
                          <input type="checkbox" checked={selected} onChange={() => {}} disabled={already}
                            className="cursor-pointer" />
                        </td>
                        <td className="p-2 font-semibold">
                          {t.descrizione}
                          {already && <span className="ml-2 text-[10px] text-emerald-700">(già presente)</span>}
                        </td>
                        <td className="p-2">{catLabel(t.categoria)}</td>
                        <td className="p-2">{PER.find(([k]) => k === t.periodicita)?.[1] || t.periodicita}</td>
                        <td className="p-2 text-center font-mono">{t.scadenza_giorno}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
              <button onClick={() => setShowTemplates(false)}
                className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">
                Annulla
              </button>
              <button
                onClick={() => {
                  const templates = Array.from(selectedTemplates).map((i) => TEMPLATES_ETHOS[i]);
                  createBulk.mutate(templates);
                }}
                disabled={selectedTemplates.size === 0 || createBulk.isPending}
                data-testid="btn-crea-da-template"
                className="px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-sm disabled:opacity-50"
              >
                {createBulk.isPending ? "Creazione…" : `Crea ${selectedTemplates.size} voci`}
              </button>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-2xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl">{tipoNuovoCosto === "gestione" ? "Nuovo costo di gestione" : "Nuovo costo fisso"}</h2>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-5 grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Descrizione</label>
                <input value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Categoria</label>
                <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
                  {CATEGORIE.map(([k,l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Periodicità</label>
                <select value={form.periodicita} onChange={(e) => setForm({ ...form, periodicita: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
                  {PER.map(([k,l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Importo imponibile €</label>
                <input type="number" step="0.01" value={form.importo_imponibile} onChange={(e) => setForm({ ...form, importo_imponibile: parseFloat(e.target.value||0) })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Aliquota IVA %</label>
                <input type="number" step="0.1" value={form.aliquota_iva} onChange={(e) => setForm({ ...form, aliquota_iva: parseFloat(e.target.value||0) })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">
                  Data inizio <span className="text-slate-400">(opz.)</span>
                </label>
                <input type="date" value={form.data_inizio || ""}
                  onChange={(e) => setForm({ ...form, data_inizio: e.target.value })}
                  data-testid="cf-data-inizio"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                <p className="text-[10px] text-slate-500 mt-1">Vuoto = valido da sempre</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">
                  Data fine <span className="text-slate-400">(opz.)</span>
                </label>
                <input type="date" value={form.data_fine || ""}
                  min={form.data_inizio || undefined}
                  onChange={(e) => setForm({ ...form, data_fine: e.target.value })}
                  data-testid="cf-data-fine"
                  className={`w-full px-3 py-2 border rounded-sm text-sm ${form.data_fine && form.data_inizio && form.data_fine < form.data_inizio ? "border-rose-500 bg-rose-50" : "border-slate-300"}`} />
                <p className="text-[10px] text-slate-500 mt-1">Vuoto = valido per sempre</p>
                {form.data_fine && form.data_inizio && form.data_fine < form.data_inizio && (
                  <p className="text-[10px] text-rose-700 font-semibold mt-1">
                    ⚠️ La data di fine non può essere precedente alla data di inizio
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Giorno scadenza</label>
                <input type="number" min="1" max="31" value={form.scadenza_giorno} onChange={(e) => setForm({ ...form, scadenza_giorno: parseInt(e.target.value||1,10) })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Metodo pagamento</label>
                <input value={form.metodo_pagamento} onChange={(e) => setForm({ ...form, metodo_pagamento: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Note</label>
                <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">Annulla</button>
              <button
                data-testid={COSTOFISSO.saveButton}
                onClick={() => {
                  // Validazione client + pulizia stringhe vuote → null
                  if (form.data_fine && form.data_inizio && form.data_fine < form.data_inizio) {
                    toast.error("La data di fine non può essere precedente alla data di inizio");
                    return;
                  }
                  create.mutate({
                    ...form,
                    tipo_costo: tipoNuovoCosto,
                    data_inizio: form.data_inizio || null,
                    data_fine: form.data_fine || null,
                  });
                }}
                className="px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-sm"
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function catLabel(k) {
  return CATEGORIE.find(c => c[0] === k)?.[1] || k;
}
function StatCard({ label, value, highlight }) {
  return (
    <div className={`bg-white border p-4 ${highlight ? "border-orange-500" : "border-slate-200"}`}>
      <div className="overline">{label}</div>
      <div className="font-display font-black text-xl mt-1">{value}</div>
    </div>
  );
}

function ImportiStoricoModal({ costo, onClose }) {
  const qc = useQueryClient();
  const currentYear = new Date().getFullYear();
  const [anno, setAnno] = React.useState(currentYear);
  const [drafts, setDrafts] = React.useState({}); // { mese: {previsto, effettivo, dirty} }
  const MESI = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];

  const { data: items = [] } = useQuery({
    queryKey: ["cf-importi", costo.costo_id],
    queryFn: () => api.get(`/costi-fissi/${costo.costo_id}/importi`).then(r => r.data),
  });

  // Mappa periodo_key YYYY-MM -> record
  const byMese = React.useMemo(() => {
    const map = {};
    for (const it of items) {
      const pk = it.periodo_key || "";
      const [yy, mm] = pk.split("-");
      if (Number(yy) === anno && mm && !isNaN(Number(mm))) {
        map[Number(mm)] = it;
      }
    }
    return map;
  }, [items, anno]);

  const save = useMutation({
    mutationFn: ({ mese, payload }) =>
      api.post(`/costi-fissi/${costo.costo_id}/importi`, payload).then(r => r.data),
    onSuccess: (_, vars) => {
      toast.success(`Importo ${MESI[vars.mese - 1]} ${anno} salvato (mesi precedenti invariati)`);
      setDrafts((d) => { const n = {...d}; delete n[vars.mese]; return n; });
      qc.invalidateQueries({ queryKey: ["cf-importi", costo.costo_id] });
      qc.invalidateQueries({ queryKey: ["costi-fissi-ripartizione"] });
      qc.invalidateQueries({ queryKey: ["costi-fissi-summary"] });
      qc.invalidateQueries({ queryKey: ["cantieri"] });
      qc.invalidateQueries({ queryKey: ["cantiere"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/costi-fissi/${costo.costo_id}/importi/${id}`).then(r => r.data),
    onSuccess: () => {
      toast.success("Importo mensile eliminato — ritorna al valore base");
      qc.invalidateQueries({ queryKey: ["cf-importi", costo.costo_id] });
      qc.invalidateQueries({ queryKey: ["costi-fissi-ripartizione"] });
      qc.invalidateQueries({ queryKey: ["costi-fissi-summary"] });
      qc.invalidateQueries({ queryKey: ["cantieri"] });
      qc.invalidateQueries({ queryKey: ["cantiere"] });
    },
  });

  const getPrev = (m) => drafts[m]?.previsto ?? byMese[m]?.importo_previsto ?? costo.importo_imponibile;
  const getEff = (m) => drafts[m]?.effettivo ?? byMese[m]?.importo_effettivo ?? "";
  const getPagato = (m) => drafts[m]?.pagato ?? byMese[m]?.pagato ?? false;
  const getDataPagamento = (m) => drafts[m]?.data_pagamento ?? byMese[m]?.data_pagamento ?? "";
  const setField = (m, k, v) => setDrafts((d) => ({...d, [m]: {...(d[m]||{}), [k]: v, dirty: true}}));

  const salvaMese = (m) => {
    const pk = `${anno}-${String(m).padStart(2, "0")}`;
    const dv = `${anno}-${String(m).padStart(2, "0")}-01`;
    save.mutate({ mese: m, payload: {
      periodo_key: pk,
      data_validita: dv,
      importo_previsto: Number(getPrev(m)) || 0,
      importo_effettivo: getEff(m) === "" || getEff(m) == null ? null : Number(getEff(m)),
      pagato: getPagato(m),
      data_pagamento: getPagato(m) ? (getDataPagamento(m) || null) : null,
      note: drafts[m]?.note || byMese[m]?.note || "",
    }});
  };

  const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="importi-storico-modal">
        <div className="p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                Modifica mensile · {PER.find(p => p[0] === costo.periodicita)?.[1]}
              </div>
              <h2 className="font-display text-xl font-black text-slate-900 truncate">
                {costo.descrizione}
              </h2>
              <div className="text-xs text-slate-500 mt-1">
                Importo base contrattuale: <b>{eur(costo.importo_imponibile)}</b> +{costo.aliquota_iva}% IVA.
                Modificando un mese, i mesi precedenti restano invariati nello storico.
              </div>
            </div>
            <button onClick={onClose} data-testid="btn-close-storico"><X className="w-5 h-5 text-slate-500" /></button>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <label className="text-xs font-semibold text-slate-600">Anno:</label>
            <select value={anno} onChange={(e) => setAnno(Number(e.target.value))}
                    data-testid="select-anno-storico"
                    className="px-2 py-1 border border-slate-300 rounded-sm text-sm">
              {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="p-5">
          <div className="overflow-x-auto border border-slate-200 rounded-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-xs uppercase text-slate-600">
                <tr>
                  <th className="text-left p-2">Mese</th>
                  <th className="text-right p-2">Importo previsto €</th>
                  <th className="text-right p-2">Importo effettivo €</th>
                  <th className="text-center p-2">Pagato</th>
                  <th className="text-center p-2">Data pagamento</th>
                  <th className="text-center p-2">Stato</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => {
                  const isDirty = !!drafts[m]?.dirty;
                  const record = byMese[m];
                  const hasOverride = !!record;
                  return (
                    <tr key={m}
                        data-testid={`storico-row-${m}`}
                        className={`border-t border-slate-100 ${isDirty ? "bg-amber-50" : hasOverride ? "bg-emerald-50/50" : ""}`}>
                      <td className="p-2 font-semibold">{MESI[m-1]} {anno}</td>
                      <td className="p-1">
                        <input type="number" step="0.01" min="0"
                          value={getPrev(m)}
                          onChange={(e) => setField(m, "previsto", e.target.value)}
                          data-testid={`storico-prev-${m}`}
                          className="w-full text-right px-2 py-1 border border-slate-200 rounded-sm font-mono" />
                      </td>
                      <td className="p-1">
                        <input type="number" step="0.01" min="0"
                          placeholder="(al pagamento)"
                          value={getEff(m)}
                          onChange={(e) => setField(m, "effettivo", e.target.value)}
                          data-testid={`storico-eff-${m}`}
                          className="w-full text-right px-2 py-1 border border-slate-200 rounded-sm font-mono" />
                      </td>
                      <td className="text-center p-2"><input type="checkbox" checked={getPagato(m)} onChange={(e) => setField(m, "pagato", e.target.checked)} aria-label={`Pagato ${MESI[m-1]} ${anno}`} /></td>
                      <td className="p-1"><input type="date" value={getDataPagamento(m)} disabled={!getPagato(m)} onChange={(e) => setField(m, "data_pagamento", e.target.value)} className="border border-slate-200 rounded-sm px-1 py-1 text-xs" /></td>
                      <td className="text-center p-2">
                        {hasOverride ? (
                          <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 rounded-sm">
                            Modificato
                          </span>
                        ) : (
                          <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 rounded-sm">
                            Base
                          </span>
                        )}
                      </td>
                      <td className="p-1 text-right whitespace-nowrap">
                        <button
                          onClick={() => salvaMese(m)}
                          disabled={!isDirty || save.isPending}
                          data-testid={`storico-save-${m}`}
                          title="Salva SOLO questo mese"
                          className="inline-flex items-center gap-1 px-2 py-1 mr-1 text-xs font-semibold bg-orange-600 hover:bg-orange-700 disabled:opacity-40 text-white rounded-sm"
                        >
                          Salva
                        </button>
                        {hasOverride && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Rimuovere l'override per ${MESI[m-1]} ${anno}? Il mese tornerà al valore base.`)) {
                                del.mutate(record.importo_id);
                              }
                            }}
                            data-testid={`storico-reset-${m}`}
                            title="Torna al valore base"
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 rounded-sm"
                          >
                            Reset
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500 mt-3">
            <b>Storico protetto:</b> ogni mese è salvato in modo indipendente. Modificando settembre,
            i mesi precedenti (es. agosto) restano con il loro importo originale. Il valore
            "Base" mostra l'importo contrattuale predefinito; "Modificato" indica che quel mese
            ha un override salvato che sovrascrive il valore base nella ripartizione mensile.
          </p>
        </div>
      </div>
    </div>
  );
}
